export const OFFLINE_CACHE_OWNER_KEY = 'inventree_offline_cache_owner';
export const OFFLINE_CACHE_WRITE_GENERATION_KEY = 'inventree_offline_cache_write_generation';
export const OFFLINE_SESSION_INVALIDATION_KEY = 'inventree_offline_session_invalidation';

const OFFLINE_SESSION_CHANNEL = 'inventree-offline-session';
const sourceId =
    globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let sessionChannel = null;

const storage = () => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const createGeneration = () =>
    globalThis.crypto?.randomUUID?.()
    ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getSessionChannel = () => {
    if (sessionChannel || typeof BroadcastChannel === 'undefined') return sessionChannel;
    try {
        sessionChannel = new BroadcastChannel(OFFLINE_SESSION_CHANNEL);
    } catch {
        sessionChannel = null;
    }
    return sessionChannel;
};

export const getOfflineCacheOwnerId = () => storage()?.getItem(OFFLINE_CACHE_OWNER_KEY) ?? null;

export const setOfflineCacheOwnerId = (ownerId) => {
    const target = storage();
    if (!target) return;
    if (ownerId === null || ownerId === undefined || ownerId === '') {
        target.removeItem(OFFLINE_CACHE_OWNER_KEY);
        return;
    }
    target.setItem(OFFLINE_CACHE_OWNER_KEY, String(ownerId));
};

export const getOfflineCacheWriteGeneration = () =>
    storage()?.getItem(OFFLINE_CACHE_WRITE_GENERATION_KEY) ?? 'legacy';

export const rotateOfflineCacheWriteGeneration = () => {
    const generation = createGeneration();
    storage()?.setItem(OFFLINE_CACHE_WRITE_GENERATION_KEY, generation);
    return generation;
};

/**
 * Publish one event to every other open tab. The storage key is written first
 * because the `storage` event is the transport every browser has; the channel
 * is the faster one where it exists.
 */
const publishCrossTab = (channel, storageKey, event) => {
    const encoded = JSON.stringify(event);

    try {
        storage()?.setItem(storageKey, encoded);
    } catch {
        // BroadcastChannel remains available when storage is unavailable.
    }

    try {
        channel?.postMessage(event);
    } catch {
        // The storage event is the compatibility fallback.
    }

    return event;
};

/**
 * Deliver each foreign event once, whichever of the two transports carries it
 * first, and never this tab's own.
 */
const subscribeCrossTab = (channel, storageKey, handler) => {
    if (typeof handler !== 'function') return () => {};

    const seen = new Set();
    const deliver = (event) => {
        if (!event || event.sourceId === sourceId || !event.id || seen.has(event.id)) return;
        seen.add(event.id);
        if (seen.size > 100) {
            const oldest = seen.values().next().value;
            seen.delete(oldest);
        }
        handler(event);
    };

    const onStorage = (event) => {
        if (event.key !== storageKey || !event.newValue) return;
        try {
            deliver(JSON.parse(event.newValue));
        } catch {
            // Ignore malformed or user-edited storage values.
        }
    };

    const onMessage = (event) => deliver(event?.data);
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    channel?.addEventListener?.('message', onMessage);

    return () => {
        if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
        channel?.removeEventListener?.('message', onMessage);
    };
};

/**
 * Tell every other open tab that the authenticated session is being cleared.
 * The shared cache-write generation is rotated by the caller before publishing
 * so an old response is stale even before the other tab handles this event.
 */
export const publishOfflineSessionInvalidation = ({
    generation,
    ownerId = null,
    reason = 'session_cleared',
} = {}) =>
    publishCrossTab(getSessionChannel(), OFFLINE_SESSION_INVALIDATION_KEY, {
        id: createGeneration(),
        sourceId,
        generation: generation ?? getOfflineCacheWriteGeneration(),
        ownerId: ownerId == null ? null : String(ownerId),
        reason,
        createdAt: Date.now(),
    });

export const subscribeOfflineSessionInvalidations = (handler) =>
    subscribeCrossTab(getSessionChannel(), OFFLINE_SESSION_INVALIDATION_KEY, handler);

// ---------------------------------------------------------------------------
// POS sessions — one per browser tab
//
// The POS drafts and the cart reservations in IndexedDB used to be one document
// and one counter shared by every tab of the app, rewritten by whichever tab
// wrote last: a second tab booted as a clone of the first, then each overwrote
// `branch_products.reserved_qty` from its own carts alone, and neither ever
// learned what the other held. A session is the unit a cart belongs to. Its id
// lives in sessionStorage, so a reload keeps it and a new tab gets its own; the
// registry below is how the other tabs tell a live session from a dead one.
// ---------------------------------------------------------------------------

export const POS_SESSION_KEY = 'inventree_pos_session';
export const POS_SESSION_REGISTRY_KEY = 'inventree_pos_sessions';
export const POS_RESERVATION_CHANGE_KEY = 'inventree_pos_reservation_change';
const POS_RESERVATION_CHANNEL = 'inventree-pos-reservations';

/** How often an open session proves it is still open. */
export const POS_SESSION_HEARTBEAT_MS = 15_000;
/**
 * A session whose last beat is older than this no longer holds stock. Chrome
 * throttles a hidden tab's timers to one wake-up a minute, so anything under
 * two minutes would drop a cashier's holds for merely switching tabs.
 */
export const POS_SESSION_LIVE_MS = 120_000;
/**
 * A session this quiet has its drafts adopted by the next POS screen to open.
 * Longer than the reservation window on purpose: a tab parked in the
 * background loses its holds quickly (nothing is lost — they come back on its
 * next reconcile) but keeps its carts unless it has been gone a long while or
 * said goodbye through `endPosSession()`.
 */
export const POS_SESSION_ADOPT_MS = 10 * 60_000;
const POS_SESSION_PRUNE_MS = 60 * 60_000;

const sessionStore = () => {
    try {
        return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
    } catch {
        return null;
    }
};

/** One per page load, so a duplicated tab (same sessionStorage) can be told apart. */
const posInstanceId = createGeneration();
let posSessionId = null;
let posSessionStarted = false;
let reservationChannel = null;

const getReservationChannel = () => {
    if (reservationChannel || typeof BroadcastChannel === 'undefined') return reservationChannel;
    try {
        reservationChannel = new BroadcastChannel(POS_RESERVATION_CHANNEL);
    } catch {
        reservationChannel = null;
    }
    return reservationChannel;
};

const readPosRegistry = () => {
    try {
        const parsed = JSON.parse(storage()?.getItem(POS_SESSION_REGISTRY_KEY) || 'null');
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writePosRegistry = (registry) => {
    try {
        storage()?.setItem(POS_SESSION_REGISTRY_KEY, JSON.stringify(registry));
    } catch {
        // A registry that cannot be written reads as "no other session is
        // open", which is the single-tab behaviour this replaces.
    }
};

const isRegistryEntryLive = (entry, now) =>
    Boolean(entry) && !entry.ended_at && now - Number(entry.beat_at || 0) <= POS_SESSION_LIVE_MS;

export const getPosSessionId = () => {
    if (posSessionId) return posSessionId;
    const store = sessionStore();
    let id = store?.getItem(POS_SESSION_KEY) || null;
    if (!id) {
        id = createGeneration();
        try {
            store?.setItem(POS_SESSION_KEY, id);
        } catch {
            // Without sessionStorage the id lives for this page load only.
        }
    }
    posSessionId = id;
    return id;
};

/**
 * Record that a session is open right now. Exported so tests can register a
 * peer; the app itself only ever beats for its own session.
 */
export const heartbeatPosSession = (sessionId = getPosSessionId(), { instance = posInstanceId, now = Date.now() } = {}) => {
    const registry = readPosRegistry();
    for (const [id, entry] of Object.entries(registry)) {
        if (now - Number(entry?.beat_at || 0) > POS_SESSION_PRUNE_MS) delete registry[id];
    }
    registry[sessionId] = { beat_at: now, instance };
    writePosRegistry(registry);
};

/** Say goodbye: the holds stop counting at once and the drafts become adoptable. */
export const endPosSession = (sessionId = getPosSessionId(), { now = Date.now() } = {}) => {
    const registry = readPosRegistry();
    if (!registry[sessionId]) return;
    registry[sessionId] = { ...registry[sessionId], ended_at: now };
    writePosRegistry(registry);
};

/** Sessions whose carts still hold stock: open, and heard from recently. */
export const livePosSessionIds = ({ now = Date.now() } = {}) =>
    Object.entries(readPosRegistry())
        .filter(([, entry]) => isRegistryEntryLive(entry, now))
        .map(([id]) => id);

export const isPosSessionLive = (sessionId, { now = Date.now() } = {}) =>
    isRegistryEntryLive(readPosRegistry()[String(sessionId)], now);

/**
 * Whether another session may take over this one's drafts: it said goodbye,
 * it has been silent for POS_SESSION_ADOPT_MS, or it was never heard from.
 */
export const isPosSessionAdoptable = (sessionId, { now = Date.now() } = {}) => {
    const entry = readPosRegistry()[String(sessionId)];
    if (!entry || entry.ended_at) return true;
    return now - Number(entry.beat_at || 0) > POS_SESSION_ADOPT_MS;
};

/**
 * Start beating for this tab. Chrome's "Duplicate" copies sessionStorage, so
 * two tabs would share an id and reserve as one; a live entry under this id
 * from another page load means this tab is the copy, and it takes a fresh id
 * before it writes anything under the old one.
 */
export const startPosSession = ({ now = Date.now() } = {}) => {
    if (posSessionStarted) return getPosSessionId();
    posSessionStarted = true;

    const entry = readPosRegistry()[getPosSessionId()];
    if (entry && entry.instance !== posInstanceId && isRegistryEntryLive(entry, now)) {
        posSessionId = createGeneration();
        try {
            sessionStore()?.setItem(POS_SESSION_KEY, posSessionId);
        } catch {
            // As in getPosSessionId: the id then lives for this page load only.
        }
    }
    heartbeatPosSession();

    if (typeof window !== 'undefined') {
        setInterval(() => heartbeatPosSession(), POS_SESSION_HEARTBEAT_MS);
        // pagehide covers close, reload, navigation away and entry into the
        // back/forward cache; pageshow undoes the last of those.
        window.addEventListener('pagehide', () => endPosSession());
        window.addEventListener('pageshow', () => heartbeatPosSession());
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') heartbeatPosSession();
        });
    }

    return getPosSessionId();
};

/** Another tab changed what it holds at `branchId`; every other tab re-derives its caps. */
export const publishPosReservationChange = ({ branchId } = {}) =>
    publishCrossTab(getReservationChannel(), POS_RESERVATION_CHANGE_KEY, {
        id: createGeneration(),
        sourceId,
        sessionId: getPosSessionId(),
        branchId: branchId == null ? null : String(branchId),
        createdAt: Date.now(),
    });

export const subscribePosReservationChanges = (handler) =>
    subscribeCrossTab(getReservationChannel(), POS_RESERVATION_CHANGE_KEY, handler);

/**
 * Returns true when any persisted identity conflicts with the user encoded in
 * the verified offline token. A malformed persisted user is treated as an
 * unknown owner and must be cleared before the token can be adopted.
 */
export const hasOfflineIdentityMismatch = ({
    tokenUserId,
    offlineSessionUserId = null,
    cacheOwnerId = null,
    storedUserRaw = null,
    currentUserId = null,
}) => {
    let storedUserId = null;
    let storedUserIdentityUnknown = false;

    if (storedUserRaw) {
        try {
            storedUserId = JSON.parse(storedUserRaw)?.id ?? null;
            storedUserIdentityUnknown = storedUserId == null;
        } catch {
            storedUserIdentityUnknown = true;
        }
    }

    if (storedUserIdentityUnknown) return true;

    return [offlineSessionUserId, cacheOwnerId, storedUserId, currentUserId]
        .filter((id) => id !== null && id !== undefined && id !== '')
        .some((id) => String(id) !== String(tokenUserId));
};
